/**
 * Global error reporter -- the browser-side counterpart to the
 * `errors-ingest` edge function + `errors.rpc_ingest_error` RPC.
 *
 * Each app calls `installGlobalErrorReporter` ONCE from `main.tsx`
 * (BEFORE `initSupabase`, so a failure during initSupabase still
 * lands in the pipeline). The installer:
 *
 *   * registers `window.onerror` and `onunhandledrejection` handlers
 *   * wraps `console.error` so it adds a breadcrumb (NOT auto-reports)
 *   * monkeypatches `fetch` to add `x-request-id` and record breadcrumbs
 *     for non-2xx responses
 *   * exposes `reportError(err, opts)` for manual reporting (rejected
 *     mutations, business-logic failures, etc.)
 *
 * Every report goes through:
 *
 *   1. In-memory dedupe on (kind, message, top_frame) for 30 seconds so
 *      a render-loop error doesn't fire 1000 network requests.
 *   2. Browser-side scrubbing via packages/core/src/scrub.ts. We never
 *      send `payload.sensitive` -- the edge function would strip it
 *      anyway, and the DB RPC drops it for non-service_role callers,
 *      but skipping it client-side means it never even reaches the wire.
 *   3. POST to `${supabaseUrl}/functions/v1/errors-ingest` with the
 *      anon key.
 *   4. Resolve with the assigned `short_code` so the boundary UI can
 *      surface it ("Reference: PCE-7F3K2A").
 *
 * Pure DOM / fetch -- no Supabase JS import. The eslint rule
 * (`no-restricted-imports` on `@supabase/supabase-js`) is honored.
 */

import { scrubObject, scrubTags, scrubText, CONTEXT_ALLOWLIST } from './scrub';

// ---------- public types --------------------------------------------------

export type ReporterApp = 'web' | 'admin' | 'marketplace' | 'marketing' | 'unknown' | string;
export type ReporterSeverity = 'fatal' | 'error' | 'warning' | 'info';

export interface ReporterActiveContext {
  userId?: string | null;
  userEmail?: string | null;
  orgId?: string | null;
  storeId?: string | null;
}

export interface InstallReporterOptions {
  app: ReporterApp;
  supabaseUrl: string;
  anonKey: string;
  release?: string | null;
  environment?: string | null;
  /** Resolve the current user/org/store context on demand. */
  getActiveContext?: () => ReporterActiveContext | null;
  /** Override the breadcrumb cap (default 50). */
  breadcrumbCap?: number;
  /** Defaults to true. Set false to skip the fetch wrapper (e.g. tests). */
  installFetchWrapper?: boolean;
  /** Defaults to true. Set false to skip the console.error wrapper. */
  installConsoleWrapper?: boolean;
  /** Defaults to true. Set false to skip window error/unhandledrejection. */
  installWindowHandlers?: boolean;
  /** Extra allowlisted context keys for this app. Merged with CONTEXT_ALLOWLIST. */
  contextAllowlist?: ReadonlyArray<string>;
}

export interface ReportErrorOptions {
  severity?: ReporterSeverity;
  tags?: Record<string, unknown>;
  context?: Record<string, unknown>;
  /** Mark a route override -- e.g. for fetch/route handlers that aren't
   *  located on `window.location.pathname`. */
  route?: string;
  /** Extra error metadata -- e.g. a parent error chain. */
  cause?: unknown;
}

export interface ReportErrorResult {
  ok: boolean;
  /** PCE-XXXXXXXX. Always present (even on failure we return a placeholder). */
  short_code: string;
  throttled?: boolean;
  new_group?: boolean;
  error?: string;
}

export interface BreadcrumbInput {
  category: 'navigation' | 'ui.click' | 'fetch' | 'console' | 'app' | 'auth' | 'query';
  message: string;
  level?: 'debug' | 'info' | 'warning' | 'error';
  data?: Record<string, unknown>;
}

// ---------- private singleton state --------------------------------------

interface ReporterState {
  options: InstallReporterOptions;
  installed: boolean;
  sessionId: string;
  breadcrumbs: Array<BreadcrumbInput & { at: string }>;
  // dedupe: key -> first-seen ms timestamp
  recentlySeen: Map<string, number>;
  contextAllowlist: ReadonlySet<string>;
  endpoint: string;
}

let state: ReporterState | null = null;

const DEDUPE_MS = 30_000;
const RECENT_SEEN_GC_THRESHOLD = 512;
const SESSION_STORAGE_KEY = 'pc.errors.sessionId';
const SHORT_CODE_FAILED = 'PCE-FAILED00';

// ---------- install -------------------------------------------------------

export function installGlobalErrorReporter(opts: InstallReporterOptions): void {
  if (state?.installed) return;
  if (typeof window === 'undefined') {
    // SSR / Astro static build -- nothing to install. Reporter functions
    // become no-ops with a synthetic short code so the same call sites
    // work in any environment.
    return;
  }

  const merged: ReporterState = {
    options: {
      breadcrumbCap: opts.breadcrumbCap ?? 50,
      installFetchWrapper: opts.installFetchWrapper ?? true,
      installConsoleWrapper: opts.installConsoleWrapper ?? true,
      installWindowHandlers: opts.installWindowHandlers ?? true,
      ...opts,
    },
    installed: true,
    sessionId: readOrCreateSessionId(),
    breadcrumbs: [],
    recentlySeen: new Map(),
    contextAllowlist: new Set<string>([
      ...CONTEXT_ALLOWLIST,
      ...(opts.contextAllowlist ?? []),
    ]),
    endpoint: `${opts.supabaseUrl.replace(/\/+$/, '')}/functions/v1/errors-ingest`,
  };
  state = merged;

  if (merged.options.installWindowHandlers) installWindowHandlers();
  if (merged.options.installFetchWrapper) installFetchWrapper();
  if (merged.options.installConsoleWrapper) installConsoleWrapper();
  installNavigationBreadcrumbs();
  installClickBreadcrumbs();
}

export function isInstalled(): boolean {
  return Boolean(state?.installed);
}

// ---------- breadcrumbs ---------------------------------------------------

export function addBreadcrumb(crumb: BreadcrumbInput): void {
  if (!state) return;
  const cap = state.options.breadcrumbCap ?? 50;
  const item = { ...crumb, at: new Date().toISOString() };
  // Scrub any free-text message + data values.
  item.message = scrubText(item.message, 512).value ?? '';
  if (item.data) {
    item.data = scrubObject(item.data, state.contextAllowlist).value as Record<string, unknown>;
  }
  state.breadcrumbs.push(item);
  if (state.breadcrumbs.length > cap) state.breadcrumbs.splice(0, state.breadcrumbs.length - cap);
}

// ---------- the public reporter API --------------------------------------

export async function reportError(
  err: unknown,
  opts: ReportErrorOptions = {},
): Promise<ReportErrorResult> {
  if (!state) {
    return { ok: false, short_code: SHORT_CODE_FAILED, error: 'reporter not installed' };
  }

  const unwrapped = unwrap(err);
  const dedupeKey = `${unwrapped.kind}|${unwrapped.message}|${unwrapped.topFrame ?? ''}`;
  const now = Date.now();
  const last = state.recentlySeen.get(dedupeKey);
  if (last && now - last < DEDUPE_MS) {
    // Same error already shipped within the last 30s -- swallow but
    // refresh the timer so a rapid burst doesn't escape after the
    // initial window closes.
    state.recentlySeen.set(dedupeKey, now);
    return { ok: true, short_code: SHORT_CODE_FAILED, error: 'deduped' };
  }
  state.recentlySeen.set(dedupeKey, now);
  if (state.recentlySeen.size > RECENT_SEEN_GC_THRESHOLD) {
    gcRecentlySeen(now);
  }

  // Build the payload.
  const allowlist = state.contextAllowlist;
  const redactedPaths: string[] = [];

  const tags = scrubTags(opts.tags ?? {}, redactedPaths).value;
  const context = scrubObject(opts.context ?? {}, allowlist, '$.context', redactedPaths)
    .value as Record<string, unknown>;
  const scrubbedMessage = scrubText(unwrapped.message, 2000);
  if (scrubbedMessage.matched.length > 0) {
    redactedPaths.push(`$.message (${scrubbedMessage.matched.join(',')})`);
  }
  const scrubbedStack = scrubText(unwrapped.stack ?? null, 32768);
  if (scrubbedStack.matched.length > 0) {
    redactedPaths.push(`$.stack (${scrubbedStack.matched.join(',')})`);
  }

  const active = state.options.getActiveContext?.() ?? null;
  const url = typeof window !== 'undefined' ? window.location.href : null;
  const route = opts.route ?? (typeof window !== 'undefined' ? window.location.pathname + window.location.search : null);
  const referrer = typeof document !== 'undefined' ? document.referrer || null : null;
  const userAgent = typeof navigator !== 'undefined' ? navigator.userAgent : null;
  const viewport =
    typeof window !== 'undefined'
      ? { w: window.innerWidth, h: window.innerHeight, dpr: window.devicePixelRatio }
      : null;

  const requestId = freshRequestId();

  const payload = {
    kind: unwrapped.kind,
    message: scrubbedMessage.value,
    stack: scrubbedStack.value,
    frames: unwrapped.frames,
    top_frame: unwrapped.topFrame,
    app: state.options.app,
    severity: opts.severity ?? 'error',
    release: state.options.release ?? null,
    environment: state.options.environment ?? 'production',
    route,
    url,
    referrer,
    user_id: active?.userId ?? null,
    user_email: active?.userEmail ?? null,
    org_id: active?.orgId ?? null,
    store_id: active?.storeId ?? null,
    request_id: requestId,
    session_id: state.sessionId,
    user_agent: userAgent,
    browser: detectBrowser(userAgent),
    os: detectOs(userAgent),
    viewport,
    breadcrumbs: state.breadcrumbs.slice(),
    context,
    tags,
    redacted_paths: redactedPaths,
    ingest_source: 'browser',
  };

  // POST to the edge function. Prefer keepalive so reports survive a
  // pagehide / navigation away.
  try {
    const res = await fetch(state.endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        apikey: state.options.anonKey,
        Authorization: `Bearer ${state.options.anonKey}`,
        'x-request-id': requestId,
      },
      body: JSON.stringify(payload),
      keepalive: true,
      // No cookies; the anon key is the credential.
      credentials: 'omit',
    });
    if (!res.ok) {
      return {
        ok: false,
        short_code: SHORT_CODE_FAILED,
        error: `errors-ingest ${res.status}`,
      };
    }
    const data = (await res.json()) as ReportErrorResult;
    return {
      ok: Boolean(data.ok),
      short_code: data.short_code || SHORT_CODE_FAILED,
      throttled: data.throttled,
      new_group: data.new_group,
      error: data.error,
    };
  } catch (e) {
    // Network failure (offline, CORS, blocked) -- still return a fake
    // short_code so the UI can show "couldn't reach support; please
    // include this code if you reach out".
     
    console.warn('[errors] reportError fetch failed:', e);
    return { ok: false, short_code: SHORT_CODE_FAILED, error: 'network' };
  }
}

// ---------- handlers ------------------------------------------------------

function installWindowHandlers(): void {
  // window.onerror -- uncaught synchronous errors.
  window.addEventListener('error', (ev: ErrorEvent) => {
    const err = ev.error ?? new Error(ev.message ?? 'window.onerror');
    void reportError(err, { severity: 'error', tags: { source: 'window.onerror' } });
  });
  // window.onunhandledrejection -- uncaught rejected promises.
  window.addEventListener('unhandledrejection', (ev: PromiseRejectionEvent) => {
    const reason = ev.reason;
    const err =
      reason instanceof Error
        ? reason
        : new Error(typeof reason === 'string' ? reason : safeJsonStringify(reason) ?? 'unhandledrejection');
    void reportError(err, {
      severity: 'error',
      tags: { source: 'unhandledrejection' },
    });
  });
}

function installFetchWrapper(): void {
  if (typeof window === 'undefined' || typeof window.fetch !== 'function') return;
  const orig = window.fetch.bind(window);
  window.fetch = async function patchedFetch(
    input: RequestInfo | URL,
    init: RequestInit = {},
  ): Promise<Response> {
    const reqId = freshRequestId();
    const headers = new Headers(init.headers ?? (input instanceof Request ? input.headers : {}));
    if (!headers.has('x-request-id')) headers.set('x-request-id', reqId);

    const startedAt = performance.now();
    const url = typeof input === 'string' ? input : input instanceof URL ? input.toString() : input.url;
    const method = (init.method ?? (input instanceof Request ? input.method : 'GET')).toUpperCase();

    let res: Response;
    try {
      res = await orig(input as RequestInfo, { ...init, headers });
    } catch (e) {
      addBreadcrumb({
        category: 'fetch',
        level: 'error',
        message: `${method} ${stripUrl(url)} -> network error`,
        data: { http_status: 0, duration_ms: Math.round(performance.now() - startedAt) },
      });
      throw e;
    }
    if (!res.ok) {
      addBreadcrumb({
        category: 'fetch',
        level: res.status >= 500 ? 'error' : 'warning',
        message: `${method} ${stripUrl(url)} -> ${res.status}`,
        data: { http_status: res.status, duration_ms: Math.round(performance.now() - startedAt) },
      });
    } else {
      addBreadcrumb({
        category: 'fetch',
        level: 'info',
        message: `${method} ${stripUrl(url)} -> ${res.status}`,
        data: { http_status: res.status, duration_ms: Math.round(performance.now() - startedAt) },
      });
    }
    return res;
  };
}

function installConsoleWrapper(): void {
  if (typeof console === 'undefined') return;
  const origError = console.error.bind(console);
  console.error = (...args: unknown[]) => {
    try {
      addBreadcrumb({
        category: 'console',
        level: 'error',
        message: args
          .map((a) => (typeof a === 'string' ? a : safeJsonStringify(a) ?? String(a)))
          .join(' ')
          .slice(0, 512),
      });
    } catch {
      /* ignore breadcrumb failures */
    }
    origError(...args);
  };
}

function installNavigationBreadcrumbs(): void {
  if (typeof window === 'undefined') return;
  const fire = (kind: string, to: string) =>
    addBreadcrumb({ category: 'navigation', level: 'info', message: `${kind} ${to}` });

  // Patch pushState / replaceState.
  const origPush = history.pushState;
  history.pushState = function patchedPushState(this: History, ...args: Parameters<History['pushState']>) {
    fire('pushState', String(args[2] ?? location.pathname));
    return origPush.apply(this, args);
  };
  const origReplace = history.replaceState;
  history.replaceState = function patchedReplaceState(this: History, ...args: Parameters<History['replaceState']>) {
    fire('replaceState', String(args[2] ?? location.pathname));
    return origReplace.apply(this, args);
  };
  window.addEventListener('popstate', () => fire('popstate', location.pathname));
  window.addEventListener('hashchange', () => fire('hashchange', location.hash));
}

function installClickBreadcrumbs(): void {
  if (typeof window === 'undefined') return;
  window.addEventListener(
    'click',
    (ev) => {
      const target = ev.target;
      if (!(target instanceof Element)) return;
      const interactive = target.closest('button, a, [role="button"], [data-track]') as HTMLElement | null;
      if (!interactive) return;
      const label =
        interactive.getAttribute('data-track') ||
        interactive.getAttribute('aria-label') ||
        interactive.textContent?.trim()?.slice(0, 80) ||
        interactive.tagName;
      addBreadcrumb({
        category: 'ui.click',
        level: 'info',
        message: `${interactive.tagName.toLowerCase()} ${label ?? ''}`.trim().slice(0, 200),
      });
    },
    { capture: true, passive: true },
  );
}

// ---------- helpers -------------------------------------------------------

interface Unwrapped {
  kind: string;
  message: string;
  stack: string | null;
  topFrame: string | null;
  frames: Array<Record<string, unknown>>;
}

function unwrap(err: unknown): Unwrapped {
  if (err instanceof Error) {
    const stack = typeof err.stack === 'string' ? err.stack : null;
    const frames = parseStackFrames(stack);
    return {
      kind: err.name || 'Error',
      message: err.message || String(err),
      stack,
      topFrame: frames[0]
        ? `${frames[0].fn ?? '<anonymous>'} (${frames[0].file ?? ''}:${frames[0].line ?? ''})`
        : null,
      frames,
    };
  }
  if (typeof err === 'string') {
    return { kind: 'Error', message: err, stack: null, topFrame: null, frames: [] };
  }
  if (err && typeof err === 'object') {
    const obj = err as Record<string, unknown>;
    return {
      kind: typeof obj.name === 'string' ? obj.name : 'Error',
      message:
        typeof obj.message === 'string'
          ? obj.message
          : safeJsonStringify(err) ?? 'Unknown error',
      stack: typeof obj.stack === 'string' ? obj.stack : null,
      topFrame: null,
      frames: [],
    };
  }
  return { kind: 'Error', message: String(err), stack: null, topFrame: null, frames: [] };
}

function parseStackFrames(stack: string | null): Array<Record<string, unknown>> {
  if (!stack) return [];
  const lines = stack.split('\n').slice(1, 51); // skip the error message line
  const out: Array<Record<string, unknown>> = [];
  for (const lineRaw of lines) {
    const line = lineRaw.trim();
    // Match patterns like:
    //   at Foo (https://app.example.com/static/js/main.abc.js:42:7)
    //   at https://app.example.com/static/js/main.abc.js:42:7
    //   Foo@https://app.example.com/static/js/main.abc.js:42:7
    const m =
      /^\s*at\s+([^(\s]+)\s*(?:\(([^)]+)\))?\s*$/.exec(line) ??
      /^\s*at\s+(.+)$/.exec(line) ??
      /^(.+?)@(.+)$/.exec(line);
    if (!m) {
      out.push({ raw: line.slice(0, 256) });
      continue;
    }
    const fn = m[1]?.includes('://') ? '<anonymous>' : m[1];
    const loc = m[2] ?? (m[1]?.includes('://') ? m[1] : '');
    const locMatch = /^(.+):(\d+):(\d+)$/.exec(loc ?? '');
    out.push({
      fn,
      file: locMatch?.[1] ?? loc ?? null,
      line: locMatch ? Number(locMatch[2]) : null,
      col: locMatch ? Number(locMatch[3]) : null,
    });
  }
  return out;
}

function safeJsonStringify(v: unknown): string | null {
  try {
    return JSON.stringify(v);
  } catch {
    return null;
  }
}

function stripUrl(url: string): string {
  try {
    const u = new URL(url, typeof window !== 'undefined' ? window.location.href : 'http://x');
    return u.pathname + (u.search ? '?…' : '');
  } catch {
    return url.slice(0, 200);
  }
}

function freshRequestId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    try {
      return crypto.randomUUID();
    } catch {
      /* fall through */
    }
  }
  // Fallback for older browsers / non-crypto contexts.
  const hex = '0123456789abcdef';
  let s = '';
  for (let i = 0; i < 32; i++) s += hex[(Math.random() * 16) | 0];
  return `${s.slice(0, 8)}-${s.slice(8, 12)}-4${s.slice(13, 16)}-${s.slice(16, 20)}-${s.slice(20, 32)}`;
}

function readOrCreateSessionId(): string {
  if (typeof window === 'undefined') return freshRequestId();
  try {
    const k = SESSION_STORAGE_KEY;
    let v = window.sessionStorage.getItem(k);
    if (!v) {
      v = freshRequestId();
      window.sessionStorage.setItem(k, v);
    }
    return v;
  } catch {
    return freshRequestId();
  }
}

function gcRecentlySeen(now: number): void {
  if (!state) return;
  for (const [k, t] of state.recentlySeen) {
    if (now - t > DEDUPE_MS) state.recentlySeen.delete(k);
  }
}

function detectBrowser(ua: string | null): Record<string, unknown> | null {
  if (!ua) return null;
  // Very rough -- enough for triage, not analytics.
  const tests: Array<[string, RegExp]> = [
    ['Edge', /Edg\/([\d.]+)/],
    ['Chrome', /Chrome\/([\d.]+)/],
    ['Firefox', /Firefox\/([\d.]+)/],
    ['Safari', /Version\/([\d.]+).*Safari/],
  ];
  for (const [name, re] of tests) {
    const m = re.exec(ua);
    if (m) return { name, version: m[1] };
  }
  return { name: 'Other', version: null };
}

function detectOs(ua: string | null): Record<string, unknown> | null {
  if (!ua) return null;
  if (/Windows NT ([\d.]+)/.test(ua)) return { name: 'Windows', version: RegExp.$1 };
  if (/Mac OS X ([\d_]+)/.test(ua)) return { name: 'macOS', version: RegExp.$1.replace(/_/g, '.') };
  if (/Android ([\d.]+)/.test(ua)) return { name: 'Android', version: RegExp.$1 };
  if (/iPhone OS ([\d_]+)/.test(ua)) return { name: 'iOS', version: RegExp.$1.replace(/_/g, '.') };
  if (/Linux/.test(ua)) return { name: 'Linux', version: null };
  return { name: 'Other', version: null };
}
